'use client';

import { Box, Theme, Typography, useMediaQuery, useTheme } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { ReactElement } from 'react';
import type { TSelectableEntityType } from '../../EntityTypeSelector/EntityTypeSelector/entityTypeGroups';
import { IdentityTypeRow, type IdentityTypeRowProps } from '../IdentityTypeRow/IdentityTypeRow';
import { NewTypeTiles } from './NewTypeTiles';
import { RequestPageEnd } from './RequestPageEnd';
import {
  resolveRequestChooseScreenCopy,
  type RequestChooseScreenCopy,
  type RequestChooseScreenCopyOverrides,
  type RequestPrimaryAction,
} from './requestChooseScreenCopy';
import { PHONE_PAGE_PADDING_BOTTOM, PHONE_PAGE_PADDING_X } from './requestChooseScreenLayout';

export type { RequestChooseScreenCopy, RequestChooseScreenCopyOverrides, RequestPrimaryAction };

/** A held type's row, as the screen needs it: the screen supplies the choice and the layout. */
export type RequestChooseScreenHeldType = Omit<
  IdentityTypeRowProps,
  'selectedId' | 'onSelect' | 'layout'
>;

export type RequestChooseScreenProps = {
  /** Who is asking. */
  providerName: string;
  /** What the request asks for, as the customer should read it, such as an entity type's name. */
  asksLabel: string;
  /**
   * The entity types the customer holds identities of, one row each, in the order to show them.
   * The screen does not sort, and does not decide which identities belong in a row.
   */
  heldTypes: RequestChooseScreenHeldType[];
  /** The offered entity types the customer holds none of. They are shown as tiles. */
  unheldTypes: { entityType: TSelectableEntityType }[];
  /**
   * True to offer "just me": the Individual tile, with the Individual tile's own words, as the
   * first of the tiles and in their radio group (verifime-adr#709). It reports `individual`
   * through `onSelect`. The parent decides whether to offer it, and never chooses it for the
   * customer. Off by default.
   */
  offersJustMe?: boolean;
  /**
   * The id of the chosen option, or null. An identity's id, `new:<entityType>` for a new one, or
   * `individual` for just me.
   * The parent owns the choice, and decides what arrives chosen. An id that is in no row and no
   * tile shows nothing as chosen.
   */
  selectedId: string | null;
  /** The chosen option's full name, shown by the action. Leave it out while nothing is chosen. */
  selectedName?: string;
  /** What the primary button does. The parent decides it from the choice. */
  primaryAction: RequestPrimaryAction;
  /** Reports the id of a tapped identity, or `new:<entityType>` for a tapped new-one option. */
  onSelect: (id: string) => void;
  onPrimary: () => void;
  onCancel: () => void;
  /**
   * Why the request limits the choice, shown as a footer at the end of the page. The parent decides
   * whether the request limits the choice; the screen shows the note when given one and nothing
   * otherwise. An empty note, or one of spaces only, counts as none. A desktop shows it under the
   * action row. A phone shows it last in the page, above the sticky bar, so it scrolls clear of the
   * bar.
   */
  limitNote?: string;
  /** Replaces any of the default strings. Strings left out keep their default. */
  copy?: RequestChooseScreenCopyOverrides;
  'data-testid'?: string;
};

type Layout = 'desktop' | 'phone';

// What differs between the two layouts in the page frame. On a desktop the page is the board's
// 1200px column; on a phone it fills the screen with a gutter, and the identity rows stay inside it.
const PAGE_BY_LAYOUT: Record<Layout, (theme: Theme) => object> = {
  desktop: (theme) => ({
    width: `calc(100% - 2 * ${theme.semantic.spacing['2lg']})`,
    maxWidth: theme.semantic.size.lg,
    marginInline: 'auto',
    paddingTop: theme.semantic.spacing.xl,
    paddingBottom: theme.semantic.spacing['4xl'],
  }),
  phone: (theme) => ({
    width: '100%',
    paddingTop: theme.semantic.spacing.lg,
    paddingInline: theme.semantic.spacing[PHONE_PAGE_PADDING_X],
    paddingBottom: theme.semantic.spacing[PHONE_PAGE_PADDING_BOTTOM],
  }),
};

const HEADER_PADDING_BY_LAYOUT: Record<Layout, (theme: Theme) => string> = {
  desktop: (theme) => `${theme.semantic.spacing.base} ${theme.semantic.spacing.lg}`,
  phone: (theme) => `${theme.semantic.spacing.sm} ${theme.semantic.spacing.base}`,
};

// The board draws the provider at 22px on a desktop and 18px on a phone. Neither has a semantic
// size: a desktop takes the `h4` size (24px) and a phone the `lg` size (18px).
const PROVIDER_SIZE_BY_LAYOUT: Record<Layout, (theme: Theme) => string> = {
  desktop: (theme) => theme.typography.h4.fontSize as string,
  phone: (theme) => theme.semantic.fontSize.lg,
};

type HeaderProps = {
  layout: Layout;
  providerName: string;
  asksLabel: string;
  copy: Pick<RequestChooseScreenCopy, 'requestFrom' | 'asksFor'>;
};

function RequestHeader({ layout, providerName, asksLabel, copy }: Readonly<HeaderProps>) {
  return (
    <Box
      sx={(theme: Theme) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: theme.semantic.spacing['2xs'],
        padding: HEADER_PADDING_BY_LAYOUT[layout](theme),
        borderRadius: theme.semantic.borderRadius.lg,
        backgroundColor: theme.palette.surface.brandSubtle,
      })}
    >
      <Typography variant="body2" component="span">
        {copy.requestFrom}
      </Typography>
      <Typography
        variant="h6"
        component="p"
        sx={(theme: Theme) => ({ margin: 0, fontSize: PROVIDER_SIZE_BY_LAYOUT[layout](theme) })}
      >
        {providerName}
      </Typography>
      <Box
        sx={(theme: Theme) => ({
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          gap: theme.semantic.spacing.xs,
        })}
      >
        <Typography variant="body1" component="span">
          {copy.asksFor}
        </Typography>
        <Typography
          variant="body1"
          component="strong"
          sx={(theme: Theme) => ({ fontWeight: theme.semantic.fontWeight.semibold })}
        >
          {asksLabel}
        </Typography>
      </Box>
    </Box>
  );
}

type TitleProps = {
  layout: Layout;
  providerName: string;
  copy: Pick<RequestChooseScreenCopy, 'pageTitle' | 'intro'>;
};

// The phone shows the title alone, as the board draws it.
function TitleBlock({ layout, providerName, copy }: Readonly<TitleProps>) {
  return (
    <>
      <Typography variant={layout === 'phone' ? 'h4' : 'h3'} component="h2">
        {copy.pageTitle}
      </Typography>
      {layout === 'desktop' && (
        <Typography variant="body1" component="p" sx={{ margin: 0 }}>
          {copy.intro(providerName)}
        </Typography>
      )}
    </>
  );
}

/**
 * The choose screen for one request: who is asking and what for, then what the customer already
 * holds as one row per type, then the types they hold none of as tiles, then the actions. A phone
 * has a sticky action bar; a desktop has an action row.
 *
 * The rows and the tiles share one choice: `selectedId` is checked on exactly one option across
 * them, and a tap on any of them reports its id through `onSelect`. Each row and the group of tiles
 * is its own radio group with its own tab stop (Tab goes from one to the next, the arrow keys move
 * within it). When the parent offers "just me" (`offersJustMe`), its tile is the first of the
 * tiles and in their radio group; it is chosen by the id `individual`.
 *
 * Presentational: the screen holds no rule about what arrives chosen, what the primary does, or
 * which identities belong in a row. The parent passes all of it.
 */
function RequestChooseScreenComponent({
  providerName,
  asksLabel,
  heldTypes,
  unheldTypes,
  offersJustMe = false,
  selectedId,
  selectedName,
  primaryAction,
  onSelect,
  onPrimary,
  onCancel,
  limitNote,
  copy: copyOverrides,
  'data-testid': dataTestId,
}: Readonly<RequestChooseScreenProps>): ReactElement {
  const theme = useTheme();
  const layout: Layout = useMediaQuery(theme.breakpoints.down('md')) ? 'phone' : 'desktop';
  const copy = resolveRequestChooseScreenCopy(copyOverrides);
  const hasHeld = heldTypes.length > 0;
  const hasTiles = unheldTypes.length > 0 || offersJustMe;

  return (
    <Box
      data-testid={dataTestId}
      sx={(pageTheme: Theme) => ({
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        gap: pageTheme.semantic.spacing['2lg'],
        ...PAGE_BY_LAYOUT[layout](pageTheme),
      })}
    >
      <RequestHeader
        layout={layout}
        providerName={providerName}
        asksLabel={asksLabel}
        copy={copy}
      />
      <TitleBlock layout={layout} providerName={providerName} copy={copy} />
      {hasHeld && (
        <>
          <Typography variant="h5" component="h3">
            {copy.heldHeading}
          </Typography>
          {heldTypes.map((heldType) => (
            <IdentityTypeRow
              key={heldType.entityType}
              {...heldType}
              layout={layout}
              selectedId={selectedId}
              onSelect={onSelect}
            />
          ))}
        </>
      )}
      {hasHeld && hasTiles && (
        <Typography variant="h5" component="h3">
          {copy.orVerifyNew}
        </Typography>
      )}
      {hasTiles && (
        <NewTypeTiles
          unheldTypes={unheldTypes}
          offersJustMe={offersJustMe}
          selectedId={selectedId}
          onSelect={onSelect}
          label={copy.orVerifyNew}
          // Under the page title (level 2) the tiles' headings are level 3, or level 4 when the held
          // heading (level 3) comes first.
          groupHeadingLevel={hasHeld ? 4 : 3}
        />
      )}
      <RequestPageEnd
        layout={layout}
        primaryAction={primaryAction}
        selectedName={selectedName}
        copy={copy}
        onPrimary={onPrimary}
        onCancel={onCancel}
        limitNote={limitNote}
      />
    </Box>
  );
}

export const RequestChooseScreen = withClarityTheme(RequestChooseScreenComponent);
